import {
  useAllowWillModification,
  useGetCustomerDetails,
  useGetCustomerWillList
} from 'common/queries-and-mutations/admin/customer';
import Button from 'components/Form/Button';
import Breadcrumb from 'components/Navigation/Breadcrumb';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ListOrder, PaginatedListMeta } from 'types/general.type';
import PDFImage from '../../../../../assets/pdf-image-1.png';
import {
  SortingState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table';
import { Loader } from 'components/Loader';
import Paginator from 'components/TableWidget/Paginator';
import clsx from 'clsx';
import withCreatePortal from 'components/HOC/withCreatePortal';
import ConfirmModal from 'components/Modal/ConfirmModal';
import { WillHistoryTypes } from 'service/dto/customer.dto';
import { format } from 'date-fns';
import SuccessModal from 'components/Modal/SuccessModal';
import WillPreviewModal from './WillPreviewModal';

const EnhancedConfirmModal = withCreatePortal(ConfirmModal);
const EnhancedSuccessModal = withCreatePortal(SuccessModal);
const EnhancedWillPreviewModal = withCreatePortal(WillPreviewModal);

export default function WillHistory() {
  const [showAskToConfirm, setShowAskToConfirm] = useState(false);
  const { customerId } = useParams();
  const navigate = useNavigate();

  const [urlParams, setUrlParams] = useSearchParams();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [willId, setWillId] = useState(0);

  const currentPage = urlParams.get('page') ?? '1';
  const numberOfItemsPerPage = urlParams.get('items_per_page') || 10;

  const { mutate, isSuccess, isError } = useAllowWillModification();

  const { data: user, refetch } = useGetCustomerDetails({
    customerId: +customerId!
  });

  const getPaginationParams = useCallback(() => {
    return {
      page: +currentPage,
      take: +numberOfItemsPerPage,
      order: ListOrder.ASC
    };
  }, [currentPage, numberOfItemsPerPage]);

  const computedWhereOptions = useCallback(() => {
    return {
      customerId: Number(customerId)
    };
  }, [customerId]);

  const {
    data: customerWills,
    isFetching,
    refetch: getWills
  } = useGetCustomerWillList({
    whereOptions: computedWhereOptions(),
    paginationOptions: getPaginationParams(),
    enabled: true
  });

  const columnHelper = createColumnHelper<any>();

  const columns = [
    columnHelper.accessor(row => row.user.surname, {
      id: 'firstName',
      cell: () => (
        <span className="text-xs font-normal">
          <img className="h-10 w-8" src={PDFImage} />
        </span>
      ),
      header: () => (
        <span className="text-xs font-semibold text-[#1F4173]"></span>
      )
    }),
    columnHelper.accessor(row => row.user.othername, {
      id: 'othername',
      cell: info => (
        <span className="text-xs font-normal">{`${info.row.original.firstName} ${info.row.original.lastName} Last Will and Testament`}</span>
      ),
      header: () => (
        <span className="text-xs font-semibold text-[#1F4173]">File Name</span>
      )
    }),
    columnHelper.accessor(row => row.version, {
      id: 'version',
      cell: info => (
        <span className="text-center text-xs font-normal">{`${info.getValue()}.0.0`}</span>
      ),
      header: () => (
        <span className="text-center text-xs font-semibold text-[#1F4173]">
          Version
        </span>
      )
    }),
    columnHelper.accessor(row => row.createdAt, {
      id: 'createdAt',
      cell: info => (
        <span className="text-xs font-normal">
          {format(new Date(info.getValue()), 'dd MMM yyyy, HH:MM')}
        </span>
      ),
      header: () => (
        <span className="text-xs font-semibold text-[#1F4173]">Shared On</span>
      )
    }),
    columnHelper.accessor(row => row.userId, {
      id: 'userId',
      cell: info => (
        <span className="text-xs font-normal">{`${info.row.original.firstName} ${info.row.original.lastName}`}</span>
      ),
      header: () => (
        <span className="text-xs font-semibold text-[#1F4173]">Shared By</span>
      )
    }),
    columnHelper.accessor(row => row.id, {
      id: 'id',
      cell: info => (
        <div>
          <div
            role="button"
            onClick={() => {
              setWillId(info.getValue());
              setShowPreviewModal(true);
            }}
            className="w-[5rem] cursor-pointer rounded border border-[#FF6600] bg-[#FF66061A] p-1 text-center text-sm font-medium text-[#FF6606]"
          >
            View
          </div>
        </div>
      ),
      header: () => (
        <span className="text-xs font-semibold text-[#1F4173]">Action</span>
      )
    })
  ];

  const data = useMemo<WillHistoryTypes[]>(() => {
    const normalizedResult: any[] = (customerWills?.data ?? []).map(
      (item, index) => ({
        id: item.id,
        staffId: item.staffId,
        userId: item.userId,
        staffName: item.staffName,
        fileName: item.fileName,
        willPath: item.willPath,
        createdAt: item.createdAt,
        email: item.user.email,
        firstName: item.user.othername,
        lastName: item.user.surname,
        phoneNumber: item.user.phoneNumber,
        version: ++index
      })
    );
    return normalizedResult;
  }, [customerWills]);

  const listMetaData = (customerWills?.meta ?? {}) as PaginatedListMeta;

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting
    },
    initialState: {
      pagination: {
        pageSize: +numberOfItemsPerPage
      }
    },
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel()
  });

  const goToNextPage = () => {
    const nextPageValue = currentPage ? `${+currentPage + 1}` : '2';
    urlParams.set('page', nextPageValue);
    setUrlParams(urlParams);
  };

  const goToPreviousPage = () => {
    if (currentPage && +currentPage > 1) {
      const previousPageValue = `${+currentPage - 1}`;
      urlParams.set('page', previousPageValue);
      setUrlParams(urlParams);
    }
  };

  useEffect(() => {
    if (isSuccess && !isError) {
      setShowAskToConfirm(false);
      setIsSubmitting(false);
      refetch();
      getWills();
    }
    if (isError) {
      setIsSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, isError]);

  const handleAllowWill = () => {
    mutate({ customerId: +customerId! });
  };

  if (isFetching) {
    return (
      <div className="mt-40 flex flex-col items-center justify-center">
        <Loader color={'#FF6606'} height={50} width={50} />
      </div>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-3">
        <div className="col-span-2 col-start-1">
          <Breadcrumb title="Customer’s Will History" />
        </div>
        {(user?.isAccountLocked ||
          user?.sentForApproval ||
          !user?.isActive) && (
          <div className="col-span-1 col-start-7 col-end-7 flex items-center gap-5">
            <Button
              variant="secondary"
              size="custom"
              className="h-5 rounded-[3px] border-[#FF6600] bg-[#FF66061A] p-4 py-5 text-sm font-medium text-[#FF6606]"
              label="Will Modification Access"
              type="button"
              disabled={isSubmitting}
              loading={isSubmitting}
              onClick={() => setShowAskToConfirm(true)}
            ></Button>
          </div>
        )}
      </div>

      <div className="mt-8 flex flex-col overflow-auto rounded-[5px] bg-white pb-10">
        <table
          className={clsx({
            'w-full min-w-full': true,
            'flexs flex-col h-[30rem]': table.getRowModel().rows.length === 0
          })}
        >
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr
                key={headerGroup.id}
                className="w-full border-b text-xs text-[#1F4173]"
              >
                {headerGroup.headers.map(header => (
                  <th
                    key={header.id}
                    className="p-5 text-left font-medium text-[#1F4173]"
                  >
                    {header.isPlaceholder ? null : (
                      <div
                        {...{
                          className: header.column.getCanSort()
                            ? 'cursor-pointer select-none flex'
                            : '',
                          onClick: header.column.getToggleSortingHandler()
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          // asc: (
                          //   <div className="flex flex-row items-center pl-2">
                          //     <Icon name={Icons.Sort} />
                          //   </div>
                          // ),
                          // desc: (
                          //   <div className="flex flex-row items-center pl-2">
                          //     <Icon name={Icons.Sort} />
                          //   </div>
                          // )
                          asc: <></>,
                          desc: <></>
                        }[header.column.getIsSorted() as string] ?? null}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 && (
              <tr className="mt-10 pt-10 text-center text-xl font-bold">
                <td colSpan={7}>No data found</td>
              </tr>
            )}

            {table.getRowModel().rows.map((row, cellIndex) => (
              <tr key={row.id} className="w-full text-xs">
                {row.getVisibleCells().map(cell => (
                  <td
                    key={cell.id}
                    className={clsx({
                      'py-5 px-3 text-xs': true,
                      'bg-[#F9F9F9]': cellIndex % 2 !== 0,
                      'w-[4%]': cell.column.id === 'firstName'
                    })}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {listMetaData && listMetaData.pageCount > 1 && (
          <div className="mt-5 p-5">
            <Paginator
              numberOfPages={listMetaData.pageCount}
              page={+currentPage}
              hasNext={listMetaData.hasNextPage}
              hasPrevious={listMetaData.hasPreviousPage}
              numberOfItemsPerPage={+numberOfItemsPerPage}
              setNumberOfItemsPerPage={num => {
                // setPageSize(num);
                urlParams.set('items_per_page', num.toString());
                setUrlParams(urlParams);
              }}
              goToNextPage={goToNextPage}
              goToPreviousPage={goToPreviousPage}
            />
          </div>
        )}
      </div>

      {showAskToConfirm && (
        <EnhancedConfirmModal
          content={`Confirm will modification access for ${user?.othername} ${user?.surname}`}
          onConfirm={() => {
            handleAllowWill();
          }}
          onCancel={() => {
            setShowAskToConfirm(false);
          }}
          actionText="Yes"
          cancelText="No"
          title="Action Required"
          isSubmitting={isSubmitting}
        />
      )}
      {showSuccess && (
        <EnhancedSuccessModal
          className="w-[25%]"
          title={`Modification Access Granted Successfully`}
          onProccessClick={() => {
            setShowSuccess(false);
            navigate(`/admin/customers/details/${customerId}`);
          }}
          buttonText="Back to Customer"
        />
      )}

      {showPreviewModal && (
        <EnhancedWillPreviewModal
          onCancel={() => {
            setShowPreviewModal(false);
          }}
          userId={Number(customerId)}
          willId={Number(willId)}
        />
      )}
    </div>
  );
}
